import React from "react";
import { useTranslation } from "react-i18next";
import { PenLine } from "lucide-react";
import { cn } from "@/lib/utils";
import { ProfileAvatar } from "@/components/common/ProfileAvatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useParticipants } from "@/hooks/inbox/useParticipants";
import { useRefreshParticipantPicture } from "@/hooks/inbox/useRefreshParticipantPicture";
import {
	formatRelativeTime,
	getChannel,
	getConversationTimestamp,
	getConversationTitle,
	getLastMessage,
	getPrimaryParticipant,
	toSnippet,
} from "./utils";
import { ChannelIcon, channelLabel } from "./ChannelBadge";
import type { InboxConversation } from "@/types/inbox";

interface ConversationListItemProps {
	conversation: InboxConversation;
	selected: boolean;
	hasDraft: boolean;
	onSelect: (id: string) => void;
}

/**
 * One scannable inbox row: avatar · name · relative time on the first line,
 * channel icon · snippet · draft/unread affordances on the second.
 */
export const ConversationListItem: React.FC<ConversationListItemProps> = React.memo(
	({ conversation, selected, hasDraft, onSelect }) => {
		const { t, i18n } = useTranslation();
		const channel = getChannel(conversation);
		const { data: participants } = useParticipants(conversation);
		const refreshPicture = useRefreshParticipantPicture();

		const title = getConversationTitle(conversation, participants);
		const primary = getPrimaryParticipant(conversation, participants);
		const lastMessage = getLastMessage(conversation);
		const snippet =
			channel === "email"
				? conversation.subject || toSnippet(lastMessage?.text)
				: toSnippet(lastMessage?.text);
		const time = formatRelativeTime(
			getConversationTimestamp(conversation),
			i18n.language,
			t("inbox.yesterday", "Yesterday")
		);
		// Only trust an unread signal when the backend actually provides one.
		const unread = typeof conversation.unread_count === "number" && conversation.unread_count > 0;

		return (
			<li role="option" aria-selected={selected}>
				<button
					type="button"
					onClick={() => onSelect(conversation.id)}
					className={cn(
						"relative flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors",
						"focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
						selected ? "bg-accent" : "hover:bg-muted/60"
					)}
				>
					{selected && <span className="absolute inset-y-1 left-0 w-0.5 rounded-full bg-primary" />}
					<ProfileAvatar
						src={primary?.picture_url}
						alt={title}
						className="h-10 w-10 shrink-0"
						participantId={primary?.id}
						conversationId={conversation.id}
						onImageError={refreshPicture}
					/>
					<div className="min-w-0 flex-1">
						<div className="flex items-baseline justify-between gap-2">
							<span
								className={cn(
									"truncate text-sm",
									unread ? "font-semibold text-foreground" : "font-medium text-foreground"
								)}
							>
								{title}
							</span>
							<span className="shrink-0 text-xs tabular-nums text-muted-foreground">{time}</span>
						</div>
						<div className="mt-0.5 flex items-center gap-1.5">
							<span className="shrink-0 text-muted-foreground/70" title={channelLabel(channel)}>
								<ChannelIcon channel={channel} className="h-3 w-3" />
							</span>
							<span className="truncate text-xs text-muted-foreground">{snippet || " "}</span>
							{hasDraft && (
								<Tooltip>
									<TooltipTrigger asChild>
										<span className="ml-auto shrink-0">
											<PenLine className="h-3 w-3 text-amber-600 dark:text-amber-500" aria-hidden />
										</span>
									</TooltipTrigger>
									<TooltipContent side="left">{t("inbox.draft", "Draft")}</TooltipContent>
								</Tooltip>
							)}
							{unread && <span className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
						</div>
					</div>
				</button>
			</li>
		);
	}
);

ConversationListItem.displayName = "ConversationListItem";
